import { useState, useEffect, useRef, useMemo, createContext, useContext } from 'react'
import "../css/EmployeeTabSA.css"
import "../css/commonStyles.css"
import moment from 'moment'
import validator from 'validator';
import ApiClient from '../ApiClient';
import { notify } from "../utils/ToastUtils";
import { useSelector, useDispatch } from "react-redux"
import { selectEmployees, fetchAllEmployees } from 'src/stores/employeeSlice';
import { selectIsApiLoading } from "../stores/loadingSlice";
import CentralizedTable from '../components/CentralizedTable';
import SkeletonLoader from '../components/SkeletonLoader';
import LoaderIcon from '../components/LoaderIcon';
import Card from "../components/Card"
import AddressProofLetter from '../letters/AddressProofLetter'
import ReactDOMServer from 'react-dom/server';
import html2pdf from 'html2pdf.js/dist/html2pdf.min';
import img from '../../src/images/DAFullLogo.png';
import onlyLogo from '../../src/images/OnlyLogo.png';
import footerimg from '../../src/images/NameLogo.png'
import ExitLetter from '../letters/ExitLetter'
import InternshipCompletionLetter from '../letters/InternshipCompletionLetter'
import { selectUser } from "../stores/userSlice";
import { User, UserPlus, UserCog, Power, Award, UserPen, ArrowRight, ChevronLeft, Zap, Users, Download, BadgeCheck, MoreVertical, Shield, Key, FileText, Mail, MapPin, Briefcase, Calendar, Hash, Lock, CircleCheck, AlertTriangle, Trash2, Edit3, Save, AlertCircle, CircleAlert, Check, Info } from 'lucide-react';
import { Button, Tooltip, Typography, IconButton, Menu, MenuItem, TextField, InputAdornment, Skeleton, Box, Autocomplete } from '@mui/material';
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AddRemoveAdmin from '../components/AddRemoveAdmin';
import ChangeReportingManager from '../components/ChangeReportingManager';
import ResetPasswordForm from '../components/ResetPasswordForm';
import Reset2FAForm from '../components/Reset2FAForm';
import UpdateUser from './UpdateUser';
import { DESIGNATIONS, ROLES, MANAGERS, ALL_LOCATIONS } from '../utils/constants';
import UserAvatar from '../components/UserAvatar';

export const LetterLoadingContext = createContext();

const ActionMenu = ({ row, handleAddress, handleExit }) => {
    const { globalLetterLoading, setGlobalLetterLoading } = useContext(LetterLoadingContext);
    const [isThisLoading, setIsThisLoading] = useState(false);

    return (
        <button
            type="button"
            disabled={globalLetterLoading}
            onClick={async (e) => {
                e.stopPropagation();
                try {
                    setGlobalLetterLoading(true);
                    setIsThisLoading(true);
                    
                    // Yield to browser's render loop so React can paint the loading spinner before html2pdf locks the main thread
                    await new Promise(resolve => setTimeout(resolve, 50));
                    
                    if (row?.STATUS === "Active") await handleAddress(row);
                    else await handleExit(row);
                } finally {
                    setIsThisLoading(false);
                    setGlobalLetterLoading(false);
                }
            }}
            className={`flex items-center justify-center gap-1.5 px-3 py-1.5 border rounded-md text-xs font-medium transition-colors whitespace-nowrap ${isThisLoading ? 'border-blue-600 text-blue-600 bg-blue-50 cursor-not-allowed' : globalLetterLoading ? 'opacity-50 cursor-not-allowed bg-slate-50 text-slate-400 border-slate-300' : 'border-blue-600 text-blue-600 hover:bg-blue-50'}`}
        >
            {isThisLoading ? (
                <div className="w-3.5 h-3.5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin shrink-0" />
            ) : (
                <Download size={14} className="shrink-0" />
            )}
            {row?.STATUS === "Active" ? "Address Proof" : "Exit Letter"}
        </button>
    );
};

const quickActionsData = [
    { id: "add_new_employee", icon: <UserPlus size={22} />, iconBg: "bg-blue-50", iconColor: "text-blue-600", title: "Add New Employee", subtitle: "Register new employee" },
    { id: "change_role", icon: <UserCog size={22} />, iconBg: "bg-violet-50", iconColor: "text-violet-600", title: "Change Role", subtitle: "Update employee role" },
    { id: "designation", icon: <BadgeCheck size={22} />, iconBg: "bg-cyan-50", iconColor: "text-cyan-600", title: "Designation", subtitle: "Manage employee designations" },
    { id: "activate_inactivate", icon: <Power size={22} />, iconBg: "bg-green-50", iconColor: "text-green-600", title: "Activate / Inactivate", subtitle: "Update employee status" },
    { id: "change_intern_status", icon: <Award size={22} />, iconBg: "bg-purple-50", iconColor: "text-purple-600", title: "Change Intern Status", subtitle: "Manage intern progress" },
    { id: "profile_update", icon: <UserPen size={22} />, iconBg: "bg-orange-50", iconColor: "text-orange-600", title: "Profile Update", subtitle: "Allow profile updates" },
    { id: "add_remove_admin", icon: <Shield size={22} />, iconBg: "bg-rose-50", iconColor: "text-rose-600", title: "Add/Remove Admin", subtitle: "Manage admin access" },
    { id: "change_reporting_manager", icon: <Users size={22} />, iconBg: "bg-emerald-50", iconColor: "text-emerald-600", title: "Change Manager", subtitle: "Update reporting line" },
    { id: "reset_password", icon: <Key size={22} />, iconBg: "bg-indigo-50", iconColor: "text-indigo-600", title: "Reset Password", subtitle: "Reset employee credentials" },
    { id: "reset_2fa", icon: <Shield size={22} />, iconBg: "bg-red-50", iconColor: "text-red-600", title: "Reset 2FA", subtitle: "Reset employee 2FA" },
    { id: "update_user_profile", icon: <FileText size={22} />, iconBg: "bg-fuchsia-50", iconColor: "text-fuchsia-600", title: "Update Profile", subtitle: "Edit employee details" }
];


function EmployeeTabSA() {
    const dispatch = useDispatch();
    const employees = useSelector(selectEmployees)
    const user = useSelector(selectUser)
    const isApiLoading = useSelector(selectIsApiLoading);
    const [rows, setRows] = useState([]);
    const columns = useMemo(() => getEmpTableColumns(), []);
    const isInitialMount = useRef(true);
    const passwordRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [globalLetterLoading, setGlobalLetterLoading] = useState(false);
    const [inactiveEmployees, setInactiveEmployees] = useState(null);
    const [isDisabled, setIsDisabled] = useState(false)
    const [emailError, setEmailError] = useState('')
    const [passwordError, setPasswordError] = useState('')
    const [disableStatus, setDisableStatus] = useState(false)
    const [disableProfile, setDisableProfile] = useState(false)
    const [previewRole, setPreviewRole] = useState(false)
    const [previewEmployee, setPreviewEmployee] = useState(false)
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [generatingLetter, setGeneratingLetter] = useState(false)
    const [IntCodeFrLetter, setIntCodeFrLetter] = useState()
    const [internList, setInternList] = useState()
    let State = require('country-state-city').State;
    const [activeEmp, setActiveEmp] = useState("Yes")
    const [currentView, setCurrentView] = useState('main');
    const initialState = {
        newEmpName: "",
        empMail: "",
        empID: "",
        reportingManager: "",
        dateOfJoin: "",
        location: "",
        empState: "",
        city: "",
        designation: "",
        password: "",
        empCode: "",
        status: "",
        exitDate: "",
        resignation: "",
        employee: "",
        allowUpdate: '',
        empRoleCode: "",
        empRole: "",
        role: "", 
        empRoleChangeDate: new Date().toISOString().split('T')[0],
        selectAll: false,
    }
    const [state, setState] = useState(initialState)
    const [internDataState, setInternDataState] = useState({
        InternCode: '',
        completionStatus: '',
        DateOfCompletion: ''
    })
    const CanadianStates = State.getAllStates().filter((el) => {
        return el.countryCode === "CA"
    }).sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))

    const IndianStates = State.getAllStates().filter((el) => {
        return el.countryCode === "IN"
    }).sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))

    const USStates = State.getAllStates().filter((el) => {
        return el.countryCode === "US"
    }).sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))

    const resetForms = () => {
        setSubmitAttempted(false);
        setState(initialState);
        setInternDataState({
            InternCode: '',
            completionStatus: '',
            DateOfCompletion: ''
        });
        setIntCodeFrLetter('');
        setEmailError('');
        setPasswordError('');
    };

    const handleBackToMain = () => {
        resetForms();
        setCurrentView('main');
    };

    const handleOpenQuickAction = (actionId) => {
        resetForms();
        setCurrentView(actionId);
        
        // Scroll to the top of the main container so the drill-down header is visible above the tabs
        const scrollContainer = document.getElementById('main-scroll-container');
        if (scrollContainer) {
            scrollContainer.scrollTop = 0;
            scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        window.scrollTo(0, 0);
    };
    
    function handleChange(evt) {
        setPreviewRole(false)
        const value = evt.target.value;
        
        let newState = {
            ...state,
            [evt.target.name]: value
        };

        if (evt.target.name === "empCode" && currentView === "activate_inactivate") {
            const allEmployees = [...(employees || []), ...(inactiveEmployees || [])];
            const selectedEmp = allEmployees.find(e => e.EMP_CODE === value);
            if (selectedEmp) {
                newState.status = selectedEmp.STATUS === 'Active' ? 'Inactive' : 'Active';
            }
        }

        setState(newState);
        if (evt.target.name === "empMail") {
            if (validator.isEmail(value)) {
                setEmailError('Valid')
            } else {
                setEmailError('Enter valid Email!')
            }
        }
        else if (evt.target.name === "password") {
            const strongRegex = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*])(?=.{8,})");
            if (strongRegex.test(value)) {
                setPasswordError('Valid')
            } else {
                setPasswordError('Needs 8 chars, 1 uppercase, 1 number, 1 special')
            }
        }
        else if (evt.target.name === "activeEmp") {
            setActiveEmp(evt.target.value)
        }
    }
    function handleInternChange(e) {
        setInternDataState({
            ...internDataState,
            [e.target.name]: e.target.value
        });
    }
    const updateEmpStatus = async () => {
        setSubmitAttempted(true);
        const isValid = state.status === 'Active' 
            ? (state.status && state.empCode)
            : (state.status && state.empCode && state.exitDate && state.resignation);

        if (isValid) {
            setDisableStatus(true)
            try {
                await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/admin/changeEmpSts`, {
                    status: state.status,
                    code: state.empCode,
                    exitDate: state.status === 'Active' ? null : moment(state.exitDate).format("YYYY-MM-DD"),
                    resignation: state.status === 'Active' ? null : moment(state.resignation).format("YYYY-MM-DD"),
                })
                getEmployeeDetails(true);
                notify.success("Status updated successfully");
                dispatch(fetchAllEmployees());
                fetchInactive();
                setDisableStatus(false)
                setState(initialState)
                setSubmitAttempted(false);
            }
            catch (error) {
                setDisableStatus(false)
                console.log(error)
                let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later"
                notify.error(errmsg)
            }
        }
    }
    function getNextEmpCode(empList, currentRole='Employee', location='India') {
        let max = 0;
        let prefix = 'DA';
        let padLength = 6;
        
        if (currentRole === 'GT') {
            prefix = 'GT';
            padLength = 6;
        } else {
            if (location === 'US') {
                prefix = 'DAUS';
                padLength = 3;
            } else if (location === 'Canada') {
                prefix = 'DACAN';
                padLength = 3;
            }
        }

        empList.filter(emp => {
            if (!emp.EMP_ID) return false;
            if (prefix === 'DA') {// For India
                // If prefix is DA, make sure it's just DA and not DAUS or DACAN
                return emp.EMP_ID.startsWith('DA') && !emp.EMP_ID.startsWith('DAUS') && !emp.EMP_ID.startsWith('DACAN');
            }
            return emp.EMP_ID.startsWith(prefix);
        }).forEach(emp => {
            const code = emp.EMP_ID;
            let numericPart = code.slice(prefix.length);
            const num = parseInt(numericPart, 10);
            if (!isNaN(num) && num > max) {
                max = num;
            }
        });

        const nextNumber = (max + 1).toString().padStart(padLength, '0');
        return prefix + nextNumber;
    }


    const updateEmpRole = async () => {
        setSubmitAttempted(true);
        if (state.empRole && state.empRoleCode && state.empRoleChangeDate ) {
            setDisableStatus(true)
            const filteredEmp = employees.filter((el) => el.EMP_CODE === state.empRoleCode)
            try {
                
                await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/admin/changeEmpRole`, {
                    role: state.empRole,
                    empID: filteredEmp[0]?.ROLE_TYPE?.toLowerCase().includes('gt') ? getNextEmpCode(employees, 'Employee', filteredEmp[0]?.EMP_COUNTRY) : filteredEmp[0]?.EMP_ID,
                    isAdmin: state.empRole?.toLowerCase().includes('admin') ? 'Yes' : 'No',
                    changeDate: moment(new Date().toISOString().split('T')[0]).format("YYYY-MM-DD"),
                    code: state.empRoleCode,
                    joiningDate: moment(state.empRoleChangeDate).format("YYYY-MM-DD"),
                })
                notify.success(`Role updated to ${state.empRole} successfully`);
                dispatch(fetchAllEmployees());
                setDisableStatus(false)
                setState(initialState)
                setPreviewRole(false)
                setSubmitAttempted(false);
            }
            catch (error) {
                setDisableStatus(false)
                console.log(error)
                let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later"
                notify.error(errmsg)
                setPreviewRole(false)
            }
        }
    }

    const allowProfileUpdate = async () => {
        setSubmitAttempted(true);
        if ((state.employee || state.selectAll) && state.allowUpdate) {
            setDisableProfile(true)
            try {
                await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/admin/allowProfileUpdate`, {
                    employee: state.employee,
                    allowUpdate: state.allowUpdate,
                    selectAll: state.selectAll
                })
                notify.success("Status updated successfully");
                dispatch(fetchAllEmployees());
                setDisableProfile(false)
                setState(initialState)
                setSubmitAttempted(false);
            }
            catch (error) {
                setDisableProfile(false)
                console.log(error)
                let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later"
                notify.error(errmsg)
            }
        }
    }

    const updateDesignation = async () => {
        setSubmitAttempted(true);
        if (state.employee && state.designation) {
            setDisableStatus(true)
            try {
                await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/admin/changeDesignation`, {
                    employee: state.employee,
                    designation: state.designation
                })
                getEmployeeDetails(true);
                notify.success("Designation updated successfully");
                dispatch(fetchAllEmployees());
                setDisableStatus(false)
                setState(initialState)
                setSubmitAttempted(false);
            }
            catch (error) {
                setDisableStatus(false)
                console.log(error)
                let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later"
                notify.error(errmsg)
            }
        }
    }


    const addNewEmployee = async () => {
        setSubmitAttempted(true);

        let currentEmailError = "Valid";
        if (state.empMail) {
            currentEmailError = validator.isEmail(state.empMail) ? 'Valid' : 'Enter valid Email!';
        }
        setEmailError(currentEmailError);

        let currentPasswordError = "Valid";
        if (state.password) {
            const strongRegex = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*])(?=.{8,})");
            currentPasswordError = strongRegex.test(state.password) ? 'Valid' : 'Needs 8 chars, 1 uppercase, 1 number, 1 special';
        }
        setPasswordError(currentPasswordError);

        if(state.newEmpName && state.empMail && state.reportingManager && state.dateOfJoin && state.location && state.empState && state.city && state.designation && state.password && state.role && currentEmailError === "Valid" && currentPasswordError === "Valid" && !previewEmployee){
            setPreviewEmployee(true)
            return
        }

        if (state.newEmpName && state.empMail && state.reportingManager && state.dateOfJoin && state.location && state.empState && state.city && state.designation && state.password && state.role) {
            if (currentEmailError === "Valid" && currentPasswordError === "Valid") {
                setIsDisabled(true)
                
                try {
                    await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/register`, {
                        empname: state.newEmpName,
                        empmail: state.empMail,
                        // empID: state.empID,
                        empID: getNextEmpCode(employees, state.role, state.location),
                        manager: state.reportingManager,
                        joiningDate: state.dateOfJoin,
                        location: state.location,
                        empState: state.empState,
                        city: state.city,
                        designation: state.designation,
                        password: state.password,
                        role:state.role
                    })
                    notify.success("New user added successfully")
                    dispatch(fetchAllEmployees());
                    setIsDisabled(false)
                    setState(initialState)
                    setSubmitAttempted(false)
                    setPreviewEmployee(false)
                    setEmailError('')
                    setPasswordError('')
                }
                catch (error) {
                    setIsDisabled(false)
                    console.log(error)
                    let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later"
                    notify.error(errmsg)
                    setPreviewEmployee(false)
                }
            } else {
                if (currentPasswordError !== "Valid" && passwordRef.current) {
                    passwordRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                setPreviewEmployee(false)
            }
        }
        else{
            if ((!state.password || currentPasswordError !== "Valid") && passwordRef.current) {
                passwordRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            setPreviewEmployee(false)
        }
    }

    function getEmpTableColumns() {
        const renderTooltipCell = (value) => (
            <Tooltip title={value || ""} arrow placement="top">
                <Typography
                    variant="body2"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        whiteSpace: "normal",
                        lineHeight: 1.4,
                        cursor: "pointer",
                    }}
                >
                    {value || "-"}
                </Typography>
            </Tooltip>
        );

        return [{
            headerName: "Emp ID",
            field: "EMP_ID",
            sortable: true,
            width: 100,
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Employee Name",
            field: "EMP_NAME",
            sortable: true,
            width: 290,
            renderCell: (params) => {
                return (
                    <div className="flex items-center gap-3 w-full">
                        <UserAvatar name={params.value} size={32} />
                        <Tooltip title={params.value || ""} arrow placement="top">
                            <Typography
                                variant="body2"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: "vertical",
                                    whiteSpace: "normal",
                                    lineHeight: 1.4,
                                    cursor: "pointer",
                                }}
                                className="font-medium text-slate-700"
                            >
                                {params.value || "-"}
                            </Typography>
                        </Tooltip>
                    </div>
                );
            }
        }, {
            headerName: "Email",
            field: "EMP_WORK_EMAIL",
            sortable: true,
            width: 300,
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Designation",
            field: "DESIGNATION",
            sortable: true,
            width: 200,
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Status",
            field: "STATUS",
            sortable: true,
            width: 100,
            renderCell: (params) => {
                const isActive = params.value === 'Active';
                return (
                    <Tooltip title={params.value || ""} arrow placement="top">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                            {params.value || '-'}
                        </span>
                    </Tooltip>
                );
            }
        }, {
            headerName: "Reporting Manager",
            field: "REPORTING_MANAGER_ID",
            sortable: true,
            width: 100,
            valueGetter: (params) => {
                const manager = MANAGERS.find(m => m.value === params.row?.REPORTING_MANAGER_ID);
                if (manager) return manager.label;
                return params.row?.REPORTING_MANAGER_ID ? params.row?.REPORTING_MANAGER_ID.split('@')[0] : "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Location",
            field: "EMP_COUNTRY",
            sortable: true,
            width: 80,
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "SSN",
            field: "SSN",
            sortable: true,
            width: 150,
            valueGetter: (params) => {
                return params.row?.SSN || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Passport Number",
            field: "PASSPORT_NO",
            sortable: true,
            width: 150,
            valueGetter: (params) => {
                return params.row?.VISA_NO || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Passport Expiry Date",
            field: "PASSPORT_EXPIRY_DT",
            sortable: true,
            width: 260,
            valueGetter: (params) => {
                return params.row?.VISA_EXPIRY_DT || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Father's Name",
            field: "EMP_FATHERS_NAME",
            sortable: true,
            width: 150,
            valueGetter: (params) => {
                return params.row?.EMP_FATHERS_NAME || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "DOB",
            field: "EMP_DATE_OF_BIRTH",
            sortable: true,
            width: 120,
            valueGetter: (params) => {
                return params.row?.EMP_DATE_OF_BIRTH ? moment(params.row?.EMP_DATE_OF_BIRTH).format("DD-MMM-YYYY") : "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Blood Group",
            field: "EMP_BLOOD_GROUP",
            sortable: true,
            width: 100,
            valueGetter: (params) => {
                return params.row?.EMP_BLOOD_GROUP || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Mobile Number",
            field: "EMP_MOBILE_NO",
            sortable: true,
            width: 120,
            valueGetter: (params) => {
                return params.row?.EMP_MOBILE_NO || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Address",
            field: "ADDRESS",
            sortable: true,
            width: 400,
            headerAlign: "center",
            valueGetter: (params) => {
                const parts = [params.row?.EMP_ADDRESS_1, params.row?.EMP_ADDRESS_2, params.row?.EMP_CITY].filter(Boolean);
                return parts.length > 0 ? parts.join(", ") : "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "State",
            field: "EMP_STATE",
            sortable: true,
            width: 120,
            valueGetter: (params) => {
                return params.row?.EMP_STATE || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "WFH",
            field: "REMOTE_EMPLOYEE",
            sortable: true,
            width: 80,
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Aadhar No.",
            field: "EMP_AADHAR_NO",
            sortable: true,
            width: 130,
            valueGetter: (params) => {
                return params.row?.EMP_AADHAR_NO || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "PAN No.",
            field: "EMP_PAN_NO",
            sortable: true,
            width: 130,
            valueGetter: (params) => {
                return params.row?.EMP_PAN_NO || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "PF/UAN No",
            field: "EMP_PF_NO",
            sortable: true,
            width: 130,
            valueGetter: (params) => {
                return params.row?.EMP_PF_NO || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Name as in Bank",
            field: "EMP_NAME_AS_IN_BANK",
            sortable: true,
            width: 200,
            valueGetter: (params) => {
                return params.row?.EMP_NAME_AS_IN_BANK || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Bank",
            field: "EMP_BANK_NAME",
            sortable: true,
            width: 80,
            valueGetter: (params) => {
                return params.row?.EMP_BANK_NAME || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Account Number",
            field: "EMP_ACCOUNT_NO",
            sortable: true,
            width: 150,
            valueGetter: (params) => {
                return params.row?.EMP_ACCOUNT_NO || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "IFSC Code",
            field: "EMP_IFSC_NO",
            sortable: true,
            width: 150,
            valueGetter: (params) => {
                return params.row?.EMP_IFSC_NO || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Personal Mail",
            field: "EMP_PERSONAL_EMAIL",
            sortable: true,
            width: 250,
            valueGetter: (params) => {
                return params.row?.EMP_PERSONAL_EMAIL || "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Date of Joining",
            field: "EMP_DATE_OF_JOIN",
            sortable: true,
            width: 120,
            valueGetter: (params) => {
                return params.row?.EMP_DATE_OF_JOIN && moment(params.row?.EMP_DATE_OF_JOIN).format("DD-MMM-YYYY") !== "Invalid date" ? moment(params.row?.EMP_DATE_OF_JOIN).format("DD-MMM-YYYY") : "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Date of Resignation",
            field: "EMP_DATE_OF_RESIGNATION",
            sortable: true,
            width: 120,
            valueGetter: (params) => {
                return params.row?.EMP_RESIGNATION_DATE && moment(params.row?.EMP_RESIGNATION_DATE).format("DD-MMM-YYYY") !== "Invalid date" ? moment(params.row?.EMP_RESIGNATION_DATE).format("DD-MMM-YYYY") : "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Date of Exit",
            field: "EMP_DATE_OF_EXIT",
            sortable: true,
            width: 120,
            valueGetter: (params) => {
                return params.row?.EMP_EXIT_DATE && moment(params.row?.EMP_EXIT_DATE).format("DD-MMM-YYYY") !== "Invalid date" ? moment(params.row?.EMP_EXIT_DATE).format("DD-MMM-YYYY") : "-";
            },
            renderCell: ({ value }) => renderTooltipCell(value)
        }, {
            headerName: "Action",
            field: "LETTER_GENERATION",
            width: 140,
            renderCell: (params) => (
                <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:"center",
                    height: "100%",
                    width: "100%",
                }}
                >
                <ActionMenu
                    row={params?.row}
                    handleAddress={handleAddress}
                    handleExit={handleExit}
                />
                </Box>
            )
        }];
    }

    const getEmployeeDetails = async (showLoader = true) => {
        if (showLoader) {
            setIsLoading(true);
            setRows([]); // Clear existing rows to force full skeleton rendering
        }
        setIsLoading(true);
        let modifiedData = [];
        if (activeEmp === "Yes") {
            if (showLoader) {
                await new Promise(resolve => setTimeout(resolve, 400)); // Artificial delay to ensure skeleton is visible
            }
            
            const active = employees.filter((el) => el.STATUS === "Active");
            if (active.length > 0) {
                setRows(active);
                setIsLoading(false);
                isInitialMount.current = false;
            } else {
                setRows([]);
                // Do NOT set isLoading to false here if there are no records, because the API might still be fetching in the background.
                // The employees useEffect will seamlessly update rows and turn off isLoading when the data arrives.
                // Add a fallback timeout in case there are genuinely 0 employees in the database.
                setTimeout(() => {
                    setIsLoading(false);
                    isInitialMount.current = false;
                }, 3000);
            }
        } else {
            if (showLoader) {
                await new Promise(resolve => setTimeout(resolve, 400)); // Artificial delay to ensure skeleton is visible
            }
            if (inactiveEmployees !== null) {
                setRows(inactiveEmployees);
                setIsLoading(false);
                isInitialMount.current = false;
            } else {
                setRows([]);
                // Fallback timeout in case fetch fails or is extremely slow
                setTimeout(() => {
                    setIsLoading(false);
                    isInitialMount.current = false;
                }, 3000);
            }
        }
    }

    async function handleAddress(row) {
        var data = {
            Name: row?.EMP_NAME,
            id: row?.EMP_ID,
            designation: row?.DESIGNATION,
            doj: row?.EMP_DATE_OF_JOIN,
            address1: row?.EMP_ADDRESS_1,
            address2: row?.EMP_ADDRESS_2,
        }
        await printHandler(<AddressProofLetter data={data} />, "Address-proof-Letter")
    }

    async function handleExit(row) {
        var data = {
            Name: row?.EMP_NAME,
            id: row?.EMP_ID,
            designation: row?.DESIGNATION,
            doj: moment(row?.EMP_DATE_OF_JOIN).format("DD-MMM-YYYY"),
            last: moment(row?.EMP_EXIT_DATE).format("DD-MMM-YYYY"),
            exit: moment(row?.EMP_RESIGNATION_DATE).format("DD-MMM-YYYY")
        }
        await printHandler(<ExitLetter data={data} />, "Exit-Letter")
    }

    async function printHandler(content, pdfName) {
        const printElement = ReactDOMServer.renderToString(content);
        // const printElement = pdfJSX();
        const opt = {
            margin: [30, 10, 30, 10],
            filename: `${pdfName}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            html2canvas: { dpi: 75, scale: 2, letterRendering: true },
            // pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
            pagebreak: { before: '.pagebreakneed', avoid: ['img', 'table', 'p', 'tr', 'th'] }
        };
        const pageBreakElements = document.getElementsByClassName('page-break');
        for (let i = 0; i < pageBreakElements.length; i++) {
            pageBreakElements[i].style.pageBreakBefore = 'always';
        }
        return await html2pdf().from(printElement).set(opt).toPdf().get('pdf').then((pdf) => {
            var totalPages = pdf.internal.getNumberOfPages();
            const pageWidth = pdf.internal.pageSize.getWidth();
            for (let i = 1; i <= totalPages; i++) {
                // set footer to every page
                pdf.setPage(i);
                // set footer font
                pdf.setFontSize(10);
                pdf.setTextColor(150);
                // this example gets internal pageSize just as an example to locate your text near the borders in case you want to do something like "Page 3 out of 4"
                pdf.text(78,
                    pdf.internal.pageSize.getHeight() - 20, 'D-ACES INDIA PRIVATE LIMITED');
                pdf.text(23,
                    pdf.internal.pageSize.getHeight() - 15, 'Gateway Office Parks - Block B6 - First Floor | 16 GST Road | Perungalathur Chennai 600063 | INDIA');
                // you can add the line separator as an image, consult the docs below to properly set the place of the image
            const topLogo = img;
            const topLogoWidth =  40;
            const topLogoHeight = 18;
            
            pdf.addImage(topLogo, 'png', (pageWidth - topLogoWidth) / 2, 5, topLogoWidth, topLogoHeight);
            pdf.addImage(footerimg, 'png', 90, pdf.internal.pageSize.getHeight() - 30, 32, 5)
            }
        }).save();
    }

    const renderStates = () => {
        switch (state?.location) {
            case "Canada": return CanadianStates?.map((state) => {
                return (
                    <MenuItem key={state.name} value={state.name}>{state.name}</MenuItem>
                )
            })
            case "India": return IndianStates?.map((state) => {
                return (
                    <MenuItem key={state.name} value={state.name}>{state.name}</MenuItem>
                )
            })
            case "US": return USStates?.map((state) => {
                return (
                    <MenuItem key={state.name} value={state.name}>{state.name}</MenuItem>
                )
            })
            default: return IndianStates?.map((state) => {
                return (
                    <MenuItem key={state.name} value={state.name}>{state.name}</MenuItem>
                )
            })
        }
    }
    const InternList = async () => {
        let data = await ApiClient.get(`${process.env.REACT_APP_BASE_URL}/admin/getIntern`)
        setInternList(data.data)
    
    }
    const updateInternStatus = async () => {
        setSubmitAttempted(true);
        if (internDataState.InternCode && internDataState.completionStatus && internDataState.DateOfCompletion) {
            setDisableStatus(true);
            try {
                await ApiClient.put(`${process.env.REACT_APP_BASE_URL}/admin/ChangeInternStatus`, internDataState)
                notify.success("Intern Status updated successfully");
                dispatch(fetchAllEmployees());
                InternList();
                setSubmitAttempted(false);
            } catch (err) {
                console.log(err)
            } finally {
                setDisableStatus(false);
            }
        }       
    }

    const InternCodeForOffer = (e) => {
        setIntCodeFrLetter(e.target.value)
    }

    const generateInternshipCompletionLetter = async () => {
        setGeneratingLetter(true);
        try {
            const selectedIntern = internList?.find(emp => emp.ID === internDataState?.InternCode);
            var link_id = selectedIntern?.LINK_ID;
            if (!link_id) {
                notify.error("Intern must have a valid record for letter generation.");
                return;
            }
            var data = await ApiClient.get(`${process.env.REACT_APP_BASE_URL}/admin/getCandidate`, { params: { token: link_id } })
            const outputUg = [
                parseUgPg(data?.data[0]?.UG_DEGREE_1),
                parseUgPg(data?.data[0]?.UG_DEGREE_2),
                parseUgPg(data?.data[0]?.UG_DEGREE_3),
                parseUgPg(data?.data[0]?.PG_DEGREE_1),
                parseUgPg(data?.data[0]?.PG_DEGREE_2),
                parseUgPg(data?.data[0]?.PG_DEGREE_3)
            ];
            const passedYears = Math.max(...outputUg?.filter((ele) => ele.Passed != "")?.map((item) => parseInt(item?.Passed)));
            const CurrentCollege = outputUg?.filter((ele) => ele.Degree != "")?.filter((obj) => obj.Passed == passedYears);
            var data = {
                Name: data?.data[0].Name,
                rollNo: data?.data[0].rollNo,
                degree: CurrentCollege[0]?.Degree,
                depart: CurrentCollege[0]?.department,
                collegename: CurrentCollege[0]?.collegeName,
                startdate: data?.data[0]?.DATE_OF_JION,
                enddate: data?.data[0]?.DATE_OF_EXIT,
            }
            printHandler(<InternshipCompletionLetter data={data} />, "Internship-Comletion-Letter")
            setIntCodeFrLetter('');
        } catch (error) {
            notify.error("Something went wrong, please try again later")
        } finally {
            setGeneratingLetter(false);
        }
    }

    const parseUgPg = (input) => {
        const [Degree, department, collegeName, Passed] = input?.split(',');
        return { Degree, department, collegeName, Passed };
    };
    useEffect(() => {
        getEmployeeDetails(true);
    }, [activeEmp])

    const fetchInactive = async () => {
        try {
            const {data} = await ApiClient.get(`${process.env.REACT_APP_BASE_URL}/admin/getInactiveEmpDetails`)
            setInactiveEmployees(data);
        } catch (err) {
            console.log(err);
            setInactiveEmployees([]);
        }
    };

    useEffect(() => {
        InternList();
        fetchInactive();
    }, []);

    useEffect(()=>{
        // If Redux employees updates, seamlessly update data without triggering loading skeleton or affecting initial load state
        if (activeEmp === "Yes" && employees?.length > 0) {
            setRows(employees.filter((el) => el.STATUS === "Active"));
            if (!isInitialMount.current) {
                setIsLoading(false); // Turn off loader when data finally arrives
            }
        } else if (activeEmp === "No" && inactiveEmployees !== null) {
            setRows(inactiveEmployees);
            if (!isInitialMount.current) {
                setIsLoading(false);
            }
        }
    },[employees, activeEmp, inactiveEmployees])
const QuickActionSkeleton = () => (
  <div className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-3">
    <div className="flex items-center gap-3 w-full">
      <Skeleton variant="rounded" width={40} height={40} className="rounded-lg shrink-0" />
      <div className="flex flex-col flex-1">
        <Skeleton variant="text" width="60%" height={20} sx={{ mt: 0.5, mb: 0 }} />
        <Skeleton variant="text" width="40%" height={16} sx={{ mt: 0.5 }} />
      </div>
    </div>
    <Skeleton variant="circular" width={18} height={18} className="shrink-0" />
  </div>
);

const QuickAction = ({
  icon,
  iconBg,
  iconColor,
  title,
  subtitle,
  onClick,
}) => (
  <button
    onClick={onClick}
    className="group flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-3 transition-all duration-200 hover:border-blue-200 hover:shadow-sm"
  >
    <div className="flex items-center gap-3">
      <div
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${iconBg} ${iconColor}`}
      >
        {icon}
      </div>

      {/* Text */}
      <div className="flex flex-col text-left">
        <h4 className="text-sm font-semibold text-slate-800 leading-5 mt-2 mb-0">
          {title}
        </h4>

        <span className="mt-0.5 text-[11px] leading-4 text-slate-500">
          {subtitle}
        </span>
      </div>
    </div>

    <ArrowRight
      size={18}
      className="flex-shrink-0 text-slate-400 transition-all duration-200 group-hover:translate-x-1 group-hover:text-blue-600"
    />
  </button>
);
    return (
        <LetterLoadingContext.Provider value={{ globalLetterLoading, setGlobalLetterLoading }}>
        <div className="flex flex-col w-full h-full flex-1 relative">
            <div 
                className={currentView === 'main' 
                    ? "block w-full h-full" 
                    : "absolute top-[-9999px] left-[-9999px] w-full opacity-0 pointer-events-none"}
            >
                <div className="flex flex-col lg:flex-row gap-3 w-full h-full">
                    {/* Left Panel: Employee Details */}
                    <div className="flex-1 flex flex-col min-w-0 w-full h-full">
                        <div 
                            className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full"
                        >
                            <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                                        <Users size={20} />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-slate-800">Employee Details</h3>
                                        <p className="text-xs text-slate-500">View and manage all employee information</p>
                                    </div>
                                </div>
                            </div>
                            
                            

                            <div className="p-0 relative flex-1 min-h-0 flex flex-col">
                                <div className="flex-1 overflow-hidden flex flex-col">
                                    {useMemo(() => (
                                        <CentralizedTable
                                            isLoading={isLoading || isApiLoading}
                                            getRowId={row => row.EMP_CODE}
                                            rows={rows}
                                            columns={columns}
                                            autoHeight
                                            renderTopToolbarCustomActions={() => (
                                                <div className="flex items-center h-12">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleChange({ target: { name: 'activeEmp', value: 'Yes' } })}
                                                        className={`relative h-full px-5 text-sm font-medium transition-colors duration-200 ${
                                                            activeEmp === 'Yes'
                                                                ? 'text-brand-600'
                                                                : 'text-slate-500 hover:text-slate-700'
                                                        }`}
                                                    >
                                                        Active
                                                        {activeEmp === 'Yes' && (
                                                            <span className="absolute bottom-0 left-0 h-[2px] w-full bg-brand-600" style={{ marginBottom: '-1px' }}></span>
                                                        )}
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleChange({ target: { name: 'activeEmp', value: 'No' } })}
                                                        className={`relative h-full px-5 text-sm font-medium transition-colors duration-200 ${
                                                            activeEmp === 'No'
                                                                ? 'text-brand-600'
                                                                : 'text-slate-500 hover:text-slate-700'
                                                        }`}
                                                    >
                                                        Inactive
                                                        {activeEmp === 'No' && (
                                                            <span className="absolute bottom-0 left-0 h-[2px] w-full bg-brand-600" style={{ marginBottom: '-1px' }}></span>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                            muiTableHeadCellProps={({ column }) => ({
                                                sx: {
                                                    boxShadow: 'none !important',
                                                    '&::before': {
                                                        boxShadow: column.id === 'LETTER_GENERATION' ? '-4px 0 10px rgba(0,0,0,0.05) !important' : 'none !important',
                                                    }
                                                }
                                            })}
                                            muiTableBodyCellProps={({ cell }) => ({
                                                sx: {
                                                    py: "8px",
                                                    borderBottom: "1px solid #e2e8f0",
                                                    px: cell.column.getIndex() === 0 ? '16px' : "3px",
                                                    overflow: cell.column.id === 'LETTER_GENERATION' ? 'visible' : 'hidden',
                                                    textOverflow: cell.column.id === 'LETTER_GENERATION' ? 'clip' : 'ellipsis',
                                                    backgroundColor: cell.column.id === 'LETTER_GENERATION' ? '#fff !important' : 'inherit',
                                                    boxShadow: 'none !important',
                                                    '&::before': {
                                                        boxShadow: cell.column.id === 'LETTER_GENERATION' ? '-4px 0 10px rgba(0,0,0,0.05) !important' : 'none !important',
                                                    }
                                                },
                                            })}
                                            initialState={{
                                                pagination: {
                                                    paginationModel: { pageSize: 10, page: 0 },
                                                },
                                                columnPinning: { right: ['LETTER_GENERATION'] }
                                            }}
                                            enableColumnPinning={true}
                                            muiTableContainerProps={{
                                                sx: {
                                                    minHeight: '450px',
                                                    height: 'auto',
                                                    flex: 1,
                                                    overflow: 'auto'
                                                },
                                            }}
                                            pageSizeOptions={[10, 25, 50]}
                                            hideFooterSelectedRowCount
                                        />
                                    ), [isLoading, isApiLoading, rows, columns, activeEmp])}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Panel: Quick Actions */}
                    <div className="w-full lg:w-[320px] xl:w-[360px] shrink-0 lg:relative flex flex-col h-full min-h-[400px]">
                        <div className="lg:absolute lg:inset-0 flex flex-col w-full h-full">
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col h-full">
    {/* Header */}
    <div className="flex items-center gap-3 mb-5 shrink-0">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
        <Zap className="h-5 w-5 text-amber-500" />
      </div>
      <div>
        <h2 className="text-[16px] font-bold text-slate-800 m-0 leading-tight">Quick Actions</h2>
        <p className="text-xs text-slate-500 font-medium mt-0.5 block m-0">Common HR operations and updates</p>

      </div>
    </div>

    <div className="space-y-5 pb-1 pt-3 overflow-y-auto flex-1 pr-1 custom-scrollbar min-h-0">
      {isLoading || isApiLoading ? (
        Array.from({ length: 9 }).map((_, idx) => (
          <QuickActionSkeleton key={idx} />
        ))
      ) : (
        <>
          {/* Action Cards */}
          {quickActionsData.map((action) => (
            <QuickAction
              key={action.id}
              icon={action.icon}
              iconBg={action.iconBg}
              iconColor={action.iconColor}
              title={action.title}
              subtitle={action.subtitle}
              onClick={() => handleOpenQuickAction(action.id)}
            />
          ))}
        </>
      )}
    </div>
  </div>
  </div>
</div>
                </div>
            </div>

            {currentView !== 'main' && (
                <div className="relative overflow-y-auto flex flex-col">
                    <div className="w-full flex-1 flex flex-col">
                    {currentView === 'add_new_employee' && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 xl:p-6 flex flex-col">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4 border-b border-slate-100 pb-3 shrink-0">
                        <div className="flex items-center gap-3">
                            <button type="button" onClick={handleBackToMain} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors -ml-2">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                            </button>
                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                                <UserPlus size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-slate-800">Add New Employee</h2>
                                <p className="text-xs text-slate-500 mt-0.5">Create a new employee profile</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button type="button" className="px-5 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm" onClick={() => { resetForms(); setPreviewEmployee(false); }}>
                                <Trash2 size={16} /> Discard
                            </button>
                            <button type="button" className={`px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`} onClick={addNewEmployee} disabled={isDisabled}>
                                {isDisabled ? <><span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>Loading...</> : previewEmployee ? 'Confirm Add Employee' : 'Preview & Save'}
                            </button>
                        </div>
                    </div>
                    
                    <div className="flex flex-col lg:flex-row gap-5">
                        {/* Left Column: Form */}
                        <div className="flex-1 space-y-4">
                            <form className="space-y-4">
                         {submitAttempted &&
                                (!state.newEmpName ||
                                    !state.empMail ||
                                    !state.reportingManager ||
                                    !state.dateOfJoin ||
                                    !state.location ||
                                    !state.empState ||
                                    !state.city ||
                                    !state.designation ||
                                    !state.password ||
                                    !state.role) && (
                                    <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600">
                                    <CircleAlert size={16} />
                                    <span>Please complete all required fields before creating the employee.</span>
                                    </div>
                                )}
                                {/* Personal Information */}
                                <div className="border border-slate-200 rounded-lg p-4 bg-white">
                                    <div className="mb-4">
                                        <h3 className="text-blue-600 font-semibold text-base mb-1">Personal Information</h3>
                                        <p className="text-xs text-slate-500">Basic personal details of the employee</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Name <span className="text-red-500">*</span></label>
                                            <TextField error={submitAttempted && !state.newEmpName} size="small" name="newEmpName" value={state.newEmpName} onChange={handleChange} placeholder="Enter Name" className={`w-full bg-white ${submitAttempted && !state.newEmpName ? '[&_fieldset]:!border-red-500' : ''}`} required InputProps={{ startAdornment: <InputAdornment position="start"><User size={16} className={submitAttempted && !state.newEmpName ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Email <span className="text-red-500">*</span></label>
                                            <TextField error={submitAttempted && (!state.empMail || (emailError !== "Valid" && emailError !== ""))} size="small" type="email" name="empMail" value={state.empMail} onChange={handleChange} placeholder="Enter Email" className={`w-full bg-white ${submitAttempted && (!state.empMail || (emailError !== "Valid" && emailError !== "")) ? '[&_fieldset]:!border-red-500' : ''}`} required InputProps={{ startAdornment: <InputAdornment position="start"><Mail size={16} className={submitAttempted && (!state.empMail || (emailError !== "Valid" && emailError !== "")) ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} />
                                            {submitAttempted && emailError !== "Valid" && emailError !== "" && <span className="text-xs text-red-500 mt-1 block">{emailError}</span>}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Work Information */}
                                <div className="border border-slate-200 rounded-lg p-4 bg-white">
                                    <div className="mb-4">
                                        <h3 className="text-blue-600 font-semibold text-base mb-1">Work Information</h3>
                                        <p className="text-xs text-slate-500">Work related details and reporting structure</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Reporting Manager <span className="text-red-500">*</span></label>
                                            <TextField error={submitAttempted && !state.reportingManager} select size="small" name="reportingManager" value={state.reportingManager} onChange={handleChange} required className={`w-full bg-white ${submitAttempted && !state.reportingManager ? '[&_fieldset]:!border-red-500' : ''}`} 
                                                 sx={{ '& .MuiOutlinedInput-root': { minHeight: '36px' }, '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px', fontSize: '14px' } }}
                                                 InputProps={{ startAdornment: <InputAdornment position="start"><UserCog size={16} className={submitAttempted && !state.reportingManager ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
                                                <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>Select Manager</MenuItem>
                                                {MANAGERS.map(manager => (
                                                    <MenuItem key={manager.value} value={manager.value} sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>{manager.label}</MenuItem>
                                                ))}
                                            </TextField>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Base Location <span className="text-red-500">*</span></label>
                                            <TextField error={submitAttempted && !state.location} select size="small" name="location" value={state.location} onChange={handleChange} required className={`w-full bg-white ${submitAttempted && !state.location ? '[&_fieldset]:!border-red-500' : ''}`} 
                                                 sx={{ '& .MuiOutlinedInput-root': { minHeight: '36px' }, '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px', fontSize: '14px' } }}
                                                 InputProps={{ startAdornment: <InputAdornment position="start"><MapPin size={16} className={submitAttempted && !state.location ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
                                                <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>Select Location</MenuItem>
                                                {ALL_LOCATIONS.map(loc => (
                                                    <MenuItem key={loc.value} value={loc.value}>{loc.label}</MenuItem>
                                                ))}
                                            </TextField>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Designation <span className="text-red-500">*</span></label>
                                            <TextField error={submitAttempted && !state.designation} select size="small" name="designation" value={state.designation} onChange={handleChange} required className={`w-full bg-white ${submitAttempted && !state.designation ? '[&_fieldset]:!border-red-500' : ''}`} 
                                                 sx={{ '& .MuiOutlinedInput-root': { minHeight: '36px' }, '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px', fontSize: '14px' } }}
                                                 InputProps={{ startAdornment: <InputAdornment position="start"><Briefcase size={16} className={submitAttempted && !state.designation ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
                                                <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>Select Designation</MenuItem>
                                                {DESIGNATIONS.map(desig => (
                                                    <MenuItem key={desig} value={desig} sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>{desig}</MenuItem>
                                                ))}
                                            </TextField>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">State <span className="text-red-500">*</span></label>
                                            <TextField error={submitAttempted && !state.empState} select size="small" name="empState" value={state.empState} onChange={handleChange} required className={`w-full bg-white ${submitAttempted && !state.empState ? '[&_fieldset]:!border-red-500' : ''}`} 
                                                 sx={{ '& .MuiOutlinedInput-root': { minHeight: '36px' }, '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px', fontSize: '14px' } }}
                                                 InputProps={{ startAdornment: <InputAdornment position="start"><MapPin size={16} className={submitAttempted && !state.empState ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
                                                <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>Select State</MenuItem>
                                                {renderStates()}
                                            </TextField>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">City <span className="text-red-500">*</span></label>
                                            <TextField error={submitAttempted && !state.city} size="small" name="city" value={state.city} onChange={handleChange} placeholder="Enter City" className={`w-full bg-white ${submitAttempted && !state.city ? '[&_fieldset]:!border-red-500' : ''}`} required InputProps={{ startAdornment: <InputAdornment position="start"><MapPin size={16} className={submitAttempted && !state.city ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Role <span className="text-red-500">*</span></label>
                                            <TextField error={submitAttempted && !state.role} select size="small" name="role" value={state.role} onChange={handleChange} required className={`w-full bg-white ${submitAttempted && !state.role ? '[&_fieldset]:!border-red-500' : ''}`} 
                                                 sx={{ '& .MuiOutlinedInput-root': { minHeight: '36px' }, '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px', fontSize: '14px' } }}
                                                 InputProps={{ startAdornment: <InputAdornment position="start"><BadgeCheck size={16} className={submitAttempted && !state.role ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
                                                <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>Select Role</MenuItem>
                                                {ROLES?.map(role => {
                                                    return (
                                                        <MenuItem key={role.value} value={role.value} sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>
                                                            {role.label}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </TextField>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Date of Joining <span className="text-red-500">*</span></label>
                                            <TextField error={submitAttempted && !state.dateOfJoin} size="small" type="date" name="dateOfJoin" value={state.dateOfJoin} onChange={handleChange} className={`w-full bg-white ${submitAttempted && !state.dateOfJoin ? '[&_fieldset]:!border-red-500' : ''}`} required InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><Calendar size={16} className={submitAttempted && !state.dateOfJoin ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} />
                                        </div>
                                    </div>
                                </div>

                                {/* Login Information */}
                                <div className="border border-slate-200 rounded-lg p-4 bg-white">
                                    <div className="mb-4">
                                        <h3 className="text-blue-600 font-semibold text-base mb-1">Login Information</h3>
                                        <p className="text-xs text-slate-500">System access credentials for the employee</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Password <span className="text-red-500">*</span></label>
                                            <TextField inputRef={passwordRef} error={submitAttempted && (!state.password || (passwordError !== "Valid" && passwordError !== ""))} size="small" name="password" value={state.password} onChange={handleChange} placeholder="Create Password" className={`w-full bg-white ${submitAttempted && (!state.password || (passwordError !== "Valid" && passwordError !== "")) ? '[&_fieldset]:!border-red-500' : ''}`} required InputProps={{ startAdornment: <InputAdornment position="start"><Lock size={16} className={submitAttempted && (!state.password || (passwordError !== "Valid" && passwordError !== "")) ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} />
                                            {submitAttempted && passwordError !== "Valid" && passwordError !== "" && <span className="text-xs text-red-500 mt-1 block">{passwordError}</span>}
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Right Column: Preview pane */}
                        {previewEmployee && (
                        <div className="w-full lg:w-[320px] xl:w-[360px] shrink-0">
                            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex flex-col h-full min-h-[500px]">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                        <UserPlus size={18} />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-slate-800">Preview Details</h3>
                                    </div>
                                </div>
                                <p className="text-xs text-slate-500 mb-6 ml-[46px] mt-[-6px]">Review before creating</p>
                                
                                <div className="space-y-4 flex-1">
                                    <div className="flex items-start gap-3">
                                        <User size={16} className="text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">NAME</p>
                                            <p className="text-sm text-slate-800 font-medium">{state.newEmpName || '-'}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start gap-3">
                                        <Mail size={16} className="text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">EMAIL</p>
                                            <p className="text-sm text-slate-800 font-medium">{state.empMail || '-'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <CircleCheck size={16} className="text-blue-500 mt-0.5" />
                                        <div>
                                            <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider mb-0.5">EMPLOYEE ID</p>
                                            <p className="text-sm text-blue-600 font-medium">{getNextEmpCode(employees, state.role, state.location) || '-'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <UserCog size={16} className="text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">REPORTING MANAGER</p>
                                            <p className="text-sm text-slate-800 font-medium">{state.reportingManager ? state.reportingManager.split('@')[0] : '-'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <Briefcase size={16} className="text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">DESIGNATION</p>
                                            <p className="text-sm text-slate-800 font-medium">{state.designation || '-'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <MapPin size={16} className="text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">LOCATION</p>
                                            <p className="text-sm text-slate-800 font-medium">{[state.city, state.empState, state.location].filter(Boolean).join(', ') || '-'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <BadgeCheck size={16} className="text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">ROLE</p>
                                            <p className="text-sm text-slate-800 font-medium">{state.role === 'GT' ? 'Graduate Trainee' : (state.role || '-')}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <Calendar size={16} className="text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">DATE OF JOINING</p>
                                            <p className="text-sm text-slate-800 font-medium">{state.dateOfJoin ? moment(state.dateOfJoin).format("DD-MMM-YYYY") : '-'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <Lock size={16} className="text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">PASSWORD</p>
                                            <p className="text-sm text-slate-800 font-medium">{state.password ? '••••••••' : '-'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 p-4 bg-blue-50/50 border border-blue-100 rounded-xl flex gap-3">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500 shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></svg>
                                    <p className="text-xs text-blue-600 leading-relaxed">Employee ID will be generated automatically after successful creation.</p>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            )}

            {currentView === 'designation' && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 xl:p-6 flex flex-col">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4 border-b border-slate-100 pb-3 shrink-0">
                        <div className="flex items-center gap-3">
                            <button type="button" onClick={handleBackToMain} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors -ml-2">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                            </button>
                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                                <Award size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-slate-800">Change Designation</h2>
                                <p className="text-xs text-slate-500 mt-0.5">Update employee designation</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button type="button" className="px-5 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm" onClick={() => setState(initialState)}>
                                <Trash2 size={16} /> Discard
                            </button>
                            <button type="button" className={`px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm ${disableStatus ? 'opacity-50 cursor-not-allowed' : ''}`} onClick={updateDesignation} disabled={disableStatus}>
                                {disableStatus ? <><span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>Loading...</> : "Update Designation"}
                            </button>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {submitAttempted && (!state.employee || !state.designation) && (
                            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600">
                                <CircleAlert size={16} />
                                <span>Please complete all required fields before updating the designation.</span>
                            </div>
                        )}
                        <div>
                            <h3 className="text-blue-600 font-semibold text-sm mb-3">1. Select Employee</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Employee <span className="text-red-500">*</span></label>
                                    <Autocomplete
                                        options={employees.filter(el => el.STATUS === "Active")}
                                        getOptionLabel={(option) => `${option.EMP_NAME} - ${option.EMP_ID}`}
                                        value={employees.find(el => el.EMP_CODE === state.employee) || null}
                                        onChange={(event, newValue) => {
                                            handleChange({ target: { name: 'employee', value: newValue ? newValue.EMP_CODE : '' } });
                                        }}
                                        size="small"
                                        className="w-full bg-white"
                                        sx={{ '& .MuiOutlinedInput-root': { padding: '2px 8px', minHeight: '36px' }, '& .MuiAutocomplete-input': { padding: '4px !important', fontSize: '14px' } }}
                                        ListboxProps={{ style: { maxHeight: 250, fontSize: '14px' } }}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params}
                                                error={submitAttempted && !state.employee}
                                                required
                                                placeholder="Search employee by name"
                                                className={submitAttempted && !state.employee ? '[&_fieldset]:!border-red-500' : ''}
                                                InputProps={{ 
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position="start" sx={{ ml: 1 }}>
                                                                <User size={16} className={submitAttempted && !state.employee ? "text-red-500" : "text-slate-400"}/>
                                                            </InputAdornment>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    )
                                                }} 
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-1 border-t border-slate-100 pt-4 mt-6">
                            <h3 className="text-blue-600 font-semibold text-sm mb-3">2. Designation Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Current Designation</label>
                                    <TextField size="small" value={employees.find(emp => emp.EMP_CODE === state.employee)?.DESIGNATION || ''} disabled className="w-full bg-slate-50" InputProps={{ startAdornment: <InputAdornment position="start"><Briefcase size={16} className="text-slate-400"/></InputAdornment> }} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">New Designation <span className="text-red-500">*</span></label>
                                    <TextField error={submitAttempted && !state.designation} select size="small" name="designation" value={state.designation} onChange={handleChange} required className={`w-full bg-white ${submitAttempted && !state.designation ? '[&_fieldset]:!border-red-500' : ''}`} 
                                         sx={{ '& .MuiOutlinedInput-root': { minHeight: '36px' }, '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px', fontSize: '14px' } }}
                                         InputProps={{ startAdornment: <InputAdornment position="start"><Briefcase size={16} className={submitAttempted && !state.designation ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
                                        <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>Select New Designation</MenuItem>
                                        {DESIGNATIONS.map(desig => {
                                            const currentEmpDesignation = employees.find(emp => emp.EMP_CODE === state.employee)?.DESIGNATION;
                                            const isDisabled = desig === currentEmpDesignation;
                                            return (
                                                <MenuItem key={desig} value={desig} disabled={isDisabled} sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>{desig}</MenuItem>
                                            );
                                        })}
                                    </TextField>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}


            {currentView === 'change_role' && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 xl:p-6 flex flex-col">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4 border-b border-slate-100 pb-3 shrink-0">
                        <div className="flex items-center gap-3">
                            <button type="button" onClick={handleBackToMain} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors -ml-2">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                            </button>
                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                                <BadgeCheck size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-slate-800">Change Role</h2>
                                <p className="text-xs text-slate-500 mt-0.5">Update employee role</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button type="button" className="px-5 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm" onClick={() => setState(initialState)}>
                                <Trash2 size={16} /> Discard
                            </button>
                            <button type="button" className={`px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm ${disableStatus ? 'opacity-50 cursor-not-allowed' : ''}`} onClick={updateEmpRole} disabled={disableStatus}>
                                {disableStatus ? <><span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>Loading...</> : "Save Changes"}
                            </button>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {submitAttempted && (!state.empRoleCode || !state.empRole || !state.empRoleChangeDate) && (
                            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600">
                                <CircleAlert size={16} />
                                <span>Please complete all required fields before updating the role.</span>
                            </div>
                        )}
                        <div>
                            <h3 className="text-blue-600 font-semibold text-sm mb-3">1. Select Employee</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Employee <span className="text-red-500">*</span></label>
                                    <Autocomplete
                                        options={employees.filter(el => el.STATUS === "Active")}
                                        getOptionLabel={(option) => `${option.EMP_NAME} - ${option.EMP_ID}`}
                                        getOptionDisabled={(option) => option?.IS_ADMIN === 'Yes'}
                                        value={employees.find(el => el.EMP_CODE === state.empRoleCode) || null}
                                        onChange={(event, newValue) => {
                                            if (newValue) {
                                                setState({
                                                    ...state,
                                                    empRoleCode: newValue.EMP_CODE,
                                                    empRole: '',
                                                    empRoleChangeDate: newValue.EMP_DATE_OF_JOIN 
                                                        ? moment(newValue.EMP_DATE_OF_JOIN).format('YYYY-MM-DD') 
                                                        : ''
                                                });
                                            } else {
                                                setState({
                                                    ...state,
                                                    empRoleCode: '',
                                                    empRole: '',
                                                    empRoleChangeDate: ''
                                                });
                                            }
                                        }}
                                        size="small"
                                        className="w-full bg-white"
                                        sx={{ '& .MuiOutlinedInput-root': { padding: '2px 8px', minHeight: '36px' }, '& .MuiAutocomplete-input': { padding: '4px !important', fontSize: '14px' } }}
                                        ListboxProps={{ style: { maxHeight: 250, fontSize: '14px' } }}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params}
                                                error={submitAttempted && !state.empRoleCode}
                                                required
                                                placeholder="Search employee by name"
                                                className={submitAttempted && !state.empRoleCode ? '[&_fieldset]:!border-red-500' : ''}
                                                InputProps={{ 
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position="start" sx={{ ml: 1 }}>
                                                                <User size={16} className={submitAttempted && !state.empRoleCode ? "text-red-500" : "text-slate-400"}/>
                                                            </InputAdornment>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    )
                                                }} 
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-1 border-t border-slate-100 pt-4 mt-6">
                            <h3 className="text-blue-600 font-semibold text-sm mb-3">2. Role Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Current Role</label>
                                    <TextField size="small" value={employees.find(emp => emp.EMP_CODE === state.empRoleCode)?.ROLE_TYPE === 'GT' ? 'Graduate Trainee' : employees.find(emp => emp.EMP_CODE === state.empRoleCode)?.ROLE_TYPE || ''} disabled className="w-full bg-slate-50" InputProps={{ startAdornment: <InputAdornment position="start"><BadgeCheck size={16} className="text-slate-400"/></InputAdornment> }} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">New Role <span className="text-red-500">*</span></label>
                                    <TextField error={submitAttempted && !state.empRole} select size="small" name="empRole" value={state.empRole} onChange={handleChange} required className={`w-full bg-white ${submitAttempted && !state.empRole ? '[&_fieldset]:!border-red-500' : ''}`} 
                                         sx={{ '& .MuiOutlinedInput-root': { minHeight: '36px' }, '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px', fontSize: '14px' } }}
                                         InputProps={{ startAdornment: <InputAdornment position="start"><BadgeCheck size={16} className={submitAttempted && !state.empRole ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
                                        <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>Select New Role</MenuItem>
                                        {ROLES.map(role => {
                                            const currentEmpRole = employees.find(emp => emp.EMP_CODE === state.empRoleCode)?.ROLE_TYPE;
                                            const isDisabled =  role.value === currentEmpRole;
                                            return (
                                                <MenuItem key={role.value} value={role.value} disabled={isDisabled} sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>
                                                    {role.label}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Date of Joining <span className="text-red-500">*</span></label>
                                    <TextField error={submitAttempted && !state.empRoleChangeDate} size="small" type="date" name="empRoleChangeDate" value={state.empRoleChangeDate} onChange={handleChange} className={`w-full bg-white ${submitAttempted && !state.empRoleChangeDate ? '[&_fieldset]:!border-red-500' : ''}`} required InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><Calendar size={16} className={submitAttempted && !state.empRoleChangeDate ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} />
                                </div>
                                {(() => {
                                    const selectedEmp = employees.find(emp => emp.EMP_CODE === state.empRoleCode);
                                    const currentEmpRole = selectedEmp?.ROLE_TYPE;
                                    const isChangingFromGT = currentEmpRole?.toLowerCase().includes('gt') && state.empRole && !state.empRole.toLowerCase().includes('gt');
                                    if (!isChangingFromGT) return null;
                                    const newEmpId = getNextEmpCode(employees, 'Employee', selectedEmp?.EMP_COUNTRY);
                                    return (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">New Employee ID <span className="text-slate-400 font-normal text-xs">(Auto-generated)</span></label>
                                            <TextField size="small" value={newEmpId || ''} disabled className="w-full bg-blue-50/50 [&_input]:!text-blue-700 [&_input]:!font-bold" InputProps={{ startAdornment: <InputAdornment position="start"><Hash size={16} className="text-blue-500"/></InputAdornment> }} />
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        
                    </div>
                </div>
            )}

            {currentView === 'activate_inactivate' && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 xl:p-6 flex flex-col">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4 border-b border-slate-100 pb-3 shrink-0">
                        <div className="flex items-center gap-3">
                            <button type="button" onClick={handleBackToMain} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors -ml-2">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                            </button>
                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                                <Power size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-slate-800">Activate / Inactivate</h2>
                                <p className="text-xs text-slate-500 mt-0.5">Update employee status</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button type="button" className="px-5 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm" onClick={() => setState(initialState)}>
                                <Trash2 size={16} /> Discard
                            </button>
                            <button type="button" className={`px-5 py-2 bg-blue-600 text-white rounded-lg transition-colors shadow-sm font-medium text-sm flex items-center justify-center gap-2 whitespace-nowrap ${disableStatus ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'}`} onClick={updateEmpStatus} disabled={disableStatus}>
                                {disableStatus ? (
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                ) : null}
                                {disableStatus ? "Loading..." : "Update Status"}
                            </button>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {submitAttempted && (!state.empCode || !state.status || (state.status === 'Inactive' && (!state.resignation || !state.exitDate))) && (
                            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600">
                                <CircleAlert size={16} />
                                <span>Please complete all required fields before updating the status.</span>
                            </div>
                        )}
                        <div>
                            <h3 className="text-blue-600 font-semibold text-sm mb-3">1. Select Employee</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Employee <span className="text-red-500">*</span></label>
                                    <Autocomplete
                                        options={[...(employees || []), ...(inactiveEmployees || [])]}
                                        getOptionLabel={(option) => `${option.EMP_NAME} - ${option.EMP_ID} (${option.STATUS})`}
                                        value={[...(employees || []), ...(inactiveEmployees || [])].find(el => el.EMP_CODE === state.empCode) || null}
                                        onChange={(event, newValue) => {
                                            handleChange({ target: { name: 'empCode', value: newValue ? newValue.EMP_CODE : '' } });
                                        }}
                                        size="small"
                                        className="w-full bg-white"
                                        sx={{ '& .MuiOutlinedInput-root': { padding: '2px 8px', minHeight: '36px' }, '& .MuiAutocomplete-input': { padding: '4px !important', fontSize: '14px' } }}
                                        ListboxProps={{ style: { maxHeight: 250, fontSize: '14px' } }}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params}
                                                error={submitAttempted && !state.empCode}
                                                required
                                                placeholder="Search employee by name"
                                                className={submitAttempted && !state.empCode ? '[&_fieldset]:!border-red-500' : ''}
                                                InputProps={{ 
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position="start" sx={{ ml: 1 }}>
                                                                <User size={16} className={submitAttempted && !state.empCode ? "text-red-500" : "text-slate-400"}/>
                                                            </InputAdornment>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    )
                                                }} 
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-1 border-t border-slate-100 pt-4 mt-4">
                            <div>
                                <h3 className="text-blue-600 font-semibold text-sm mb-1">2. Status Update</h3>
                                <p className="text-xs text-slate-500 mb-2">(Status is automatically determined based on selected employee)</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">New Status <span className="text-red-500">*</span></label>
                                    <div className="flex space-x-4 mt-2">
                                        <label className="flex items-center space-x-2 cursor-not-allowed" onClick={(e) => e.preventDefault()}>
                                            <input type="radio" name="status" value="Active" checked={state.status === 'Active'} readOnly className="w-4 h-4 text-brand-600 focus:ring-brand-500 border-gray-300 cursor-not-allowed pointer-events-none" />
                                            <span className="text-sm font-medium text-slate-700">Activate</span>
                                        </label>
                                        <label className="flex items-center space-x-2 cursor-not-allowed" onClick={(e) => e.preventDefault()}>
                                            <input type="radio" name="status" value="Inactive" checked={state.status === 'Inactive'} readOnly className="w-4 h-4 text-brand-600 focus:ring-brand-500 border-gray-300 cursor-not-allowed pointer-events-none" />
                                            <span className="text-sm font-medium text-slate-700">Inactivate</span>
                                        </label>
                                    </div>
                                </div>
                                {state.status === 'Inactive' && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Date of Resignation <span className="text-red-500">*</span></label>
                                            <TextField error={submitAttempted && !state.resignation} size="small" type="date" name="resignation" value={state.resignation} onChange={handleChange} className={`w-full bg-white ${submitAttempted && !state.resignation ? '[&_fieldset]:!border-red-500' : ''}`} required InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><Calendar size={16} className={submitAttempted && !state.resignation ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Date of Exit <span className="text-red-500">*</span></label>
                                            <TextField error={submitAttempted && !state.exitDate} size="small" type="date" name="exitDate" value={state.exitDate} onChange={handleChange} className={`w-full bg-white ${submitAttempted && !state.exitDate ? '[&_fieldset]:!border-red-500' : ''}`} required InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><Calendar size={16} className={submitAttempted && !state.exitDate ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {currentView === 'change_intern_status' && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 xl:p-6 flex flex-col">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-3 shrink-0">
                        <div className="flex items-center gap-3">
                            <button type="button" onClick={handleBackToMain} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors -ml-2">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                            </button>
                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                                <FileText size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-slate-800">Change Intern Status</h2>
                                <p className="text-xs text-slate-500 mt-0.5">Update internship completion status and generate completion letter</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button type="button" className="px-5 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm" onClick={resetForms}>
                                <Trash2 size={16} /> Discard
                            </button>
                        </div>
                    </div>

                    <div className="w-full">
                        {submitAttempted && (!internDataState.InternCode || !internDataState.completionStatus || !internDataState.DateOfCompletion) && (
                            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 mb-6">
                                <CircleAlert size={16} />
                                <span>Please complete all required fields before updating the intern status.</span>
                            </div>
                        )}
                        
                        <div className="relative space-y-6">
                            {/* Vertical Line */}
                            <div className="absolute left-[15px] top-6 bottom-10 w-[2px] bg-slate-200 z-0"></div>

                            {/* 1. Select Intern */}
                            <div className="relative z-10">
                                <div className="flex absolute left-0 top-4 w-8 h-8 rounded-full bg-blue-600 text-white items-center justify-center font-bold text-sm shadow-sm ring-4 ring-white z-10">1</div>
                                <div className="ml-10 md:ml-12 border border-slate-200 rounded-lg p-4 md:p-5 bg-white shadow-sm relative">
                                    <h3 className="text-blue-600 font-semibold text-sm md:text-base mb-4 flex items-center">Select Intern</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 mb-1">Intern Name <span className="text-red-500">*</span></label>
                                    <TextField error={submitAttempted && !internDataState?.InternCode} select size="small" name="InternCode" value={internDataState?.InternCode} onChange={handleInternChange} required className={`w-full bg-white ${submitAttempted && !internDataState?.InternCode ? '[&_fieldset]:!border-red-500' : ''}`} 
                                         sx={{ '& .MuiOutlinedInput-root': { minHeight: '36px' }, '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px', fontSize: '14px' } }}
                                         InputProps={{ startAdornment: <InputAdornment position="start"><User size={16} className={submitAttempted && !internDataState?.InternCode ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
                                        <MenuItem value="" sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }} disabled>Search intern by name</MenuItem>
                                        {internList && internList.filter((el) => el.TYPE === "Intern").map((emp) => (
                                            <MenuItem key={emp.ID} value={emp.ID} sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>{emp?.NAME + ' - ' + emp?.INTERN_CODE}</MenuItem>
                                        ))}
                                    </TextField>
                                </div>
                            </div>
                        </div>
                        </div>

                        {/* 2. Update Internship Status */}
                        {(() => {
                            const selectedIntern = internList?.find(el => el.ID === internDataState?.InternCode);
                            const isCompleted = selectedIntern?.COMPLETION_STATUS === 'Yes';
                            const isActive = !!internDataState.InternCode;
                            return (
                                <div className={`relative z-10 ${!isActive ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <div className={`flex absolute left-0 top-4 w-8 h-8 rounded-full items-center justify-center font-bold text-sm shadow-sm ring-4 ring-white z-10 ${isActive ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>2</div>
                                    <div className="ml-10 md:ml-12 border border-slate-200 rounded-lg p-4 md:p-5 bg-white shadow-sm relative">
                                        <h3 className={`font-semibold text-sm md:text-base mb-4 flex items-center ${isActive ? 'text-blue-600' : 'text-slate-500'}`}>Update Internship Status</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 mb-1">Internship Completion Status <span className="text-red-500">*</span></label>
                                            <TextField disabled={isCompleted} error={submitAttempted && !internDataState?.completionStatus} select size="small" name="completionStatus" value={isCompleted ? 'Yes' : internDataState?.completionStatus} onChange={handleInternChange} required className={`w-full bg-white ${submitAttempted && !internDataState?.completionStatus ? '[&_fieldset]:!border-red-500' : ''}`} 
                                                sx={{ '& .MuiOutlinedInput-root': { minHeight: '36px' }, '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px', fontSize: '14px' } }}
                                                InputProps={{ startAdornment: <InputAdornment position="start"><CircleCheck size={16} className={submitAttempted && !internDataState?.completionStatus ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
                                                <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>Select Status</MenuItem>
                                                <MenuItem value="Yes" sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>Yes</MenuItem>
                                                <MenuItem value="No" sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>No</MenuItem>
                                            </TextField>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 mb-1">Date of Completion <span className="text-red-500">*</span></label>
                                            <TextField disabled={isCompleted} error={submitAttempted && !internDataState?.DateOfCompletion} size="small" type="date" name="DateOfCompletion" value={isCompleted && selectedIntern?.DATE_OF_EXIT ? moment(selectedIntern.DATE_OF_EXIT).format('YYYY-MM-DD') : internDataState?.DateOfCompletion} onChange={handleInternChange} className={`w-full bg-white ${submitAttempted && !internDataState?.DateOfCompletion ? '[&_fieldset]:!border-red-500' : ''}`} required InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><Calendar size={16} className={submitAttempted && !internDataState?.DateOfCompletion ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} />
                                        </div>
                                    </div>
                                    <div className="flex flex-col md:flex-row items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-100 gap-4">
                                        <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                                            <CircleCheck size={18} className="text-green-600" />
                                            {isCompleted ? "Internship status is completed and letter can be generated." : "Internship status will be saved and you can proceed to generate the completion letter."}
                                        </div>
                                        {!isCompleted && (
                                            <button type="button" className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm shrink-0 ${disableStatus ? 'opacity-50 cursor-not-allowed' : ''}`} onClick={updateInternStatus} disabled={disableStatus}>
                                                {disableStatus ? <><LoaderIcon /> Loading...</> : "Save Status"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                </div>
                            );
                        })()}

                        {/* 3. Generate Completion Letter */}
                        {(() => {
                            const selectedIntern = internList?.find(el => el.ID === internDataState?.InternCode);
                            const isCompleted = selectedIntern?.COMPLETION_STATUS === 'Yes';
                            return (
                                <div className={`relative z-10 ${!isCompleted ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <div className={`flex absolute left-0 top-4 w-8 h-8 rounded-full items-center justify-center font-bold text-sm shadow-sm ring-4 ring-white z-10 ${isCompleted ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>3</div>
                                    <div className="ml-10 md:ml-12 border border-slate-200 rounded-lg p-4 md:p-5 bg-white shadow-sm relative">
                                        <h3 className={`font-semibold text-sm md:text-base mb-4 flex items-center ${isCompleted ? 'text-blue-600' : 'text-slate-500'}`}>Generate Completion Letter</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4 items-center bg-slate-50 p-4 rounded-lg">
                                        <div className="flex items-center gap-4">
                                            <div className="relative">
                                                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
                                                    <User size={24} />
                                                </div>
                                                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center border-2 border-white">
                                                    <Check size={12} className="text-white" />
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-500 font-medium">Intern Name</p>
                                                <p className="font-semibold text-slate-800 text-sm">{selectedIntern ? `${selectedIntern.NAME} - ${selectedIntern.INTERN_CODE}` : '-'}</p>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500 font-medium text-center md:text-left">Internship Status</p>
                                            <div className="mt-1 flex justify-center md:justify-start">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    Completed
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500 font-medium text-center md:text-left">Date of Completion</p>
                                            <p className="font-semibold text-slate-800 text-sm text-center md:text-left mt-1">
                                                {selectedIntern?.DATE_OF_EXIT ? moment(selectedIntern.DATE_OF_EXIT).format('DD-MM-YYYY') : '-'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col md:flex-row items-center justify-between mt-4 gap-4">
                                        <div className="flex items-center gap-2 text-slate-600 text-sm font-medium">
                                            <Info size={16} className="text-blue-500" />
                                            You can now generate the completion letter for this intern.
                                        </div>
                                        <button type="button" onClick={generateInternshipCompletionLetter} disabled={!isCompleted || generatingLetter} className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm shadow-sm flex items-center gap-2 shrink-0 ${!isCompleted || generatingLetter ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                            {generatingLetter ? <><LoaderIcon /> Generating...</> : <><FileText size={16} /> Generate Letter</>}
                                        </button>
                                    </div>
                                </div>
                                </div>
                            );
                        })()}
                        </div>
                    </div>
                </div>
            )}

            {currentView === 'profile_update' && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 xl:p-6 flex flex-col">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4 border-b border-slate-100 pb-3 shrink-0">
                        <div className="flex items-center gap-3">
                            <button type="button" onClick={handleBackToMain} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors -ml-2">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                            </button>
                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                                <Edit3 size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-slate-800">Profile Update</h2>
                                <p className="text-xs text-slate-500 mt-0.5">Allow employee profile updates</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button type="button" className="px-5 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm" onClick={() => setState(initialState)}>
                                <Trash2 size={16} /> Discard
                            </button>
                            <button type="button" className={`px-5 py-2 bg-blue-600 text-white rounded-lg transition-colors shadow-sm font-medium text-sm flex items-center justify-center gap-2 whitespace-nowrap ${disableProfile ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'}`} onClick={allowProfileUpdate} disabled={disableProfile}>
                                {disableProfile ? (
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                ) : null}
                                {disableProfile ? "Loading..." : "Update Settings"}
                            </button>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {submitAttempted && (!state.employee && !state.selectAll || !state.allowUpdate) && (
                            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600">
                                <CircleAlert size={16} />
                                <span>Please complete all required fields before updating the designation.</span>
                            </div>
                        )}
                        <div>
                            <h3 className="text-blue-600 font-semibold text-sm mb-3">1. Select Employee</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Employee Name <span className="text-red-500">*</span>
                                    </label>
                                    <Autocomplete
                                        options={[
                                            { EMP_CODE: "ALL", EMP_NAME: "Select All Employees", EMP_ID: "" },
                                            ...(employees?.filter(el => el.STATUS === "Active") || [])
                                        ]}
                                        getOptionLabel={(option) => option.EMP_CODE === "ALL" ? option.EMP_NAME : `${option.EMP_NAME} - ${option.EMP_ID}`}
                                        value={
                                            state.selectAll 
                                                ? { EMP_CODE: "ALL", EMP_NAME: "Select All Employees", EMP_ID: "" }
                                                : (employees?.find(el => el.EMP_CODE === state.employee) || null)
                                        }
                                        onChange={(event, newValue) => {
                                            if (!newValue) {
                                                setState({...state, selectAll: false, employee: '', allowUpdate: ''});
                                            } else if (newValue.EMP_CODE === "ALL") {
                                                setState({...state, selectAll: true, employee: '', allowUpdate: ''});
                                            } else {
                                                const selectedEmp = employees?.find(el => el.EMP_CODE === newValue.EMP_CODE);
                                                const currentStatus = selectedEmp?.ALLOW_EDIT;
                                                const newStatus = currentStatus === "Yes" ? "No" : "Yes";
                                                setState({...state, selectAll: false, employee: newValue.EMP_CODE, allowUpdate: newStatus});
                                            }
                                        }}
                                        size="small"
                                        className="w-full bg-white"
                                        sx={{ '& .MuiOutlinedInput-root': { padding: '2px 8px', minHeight: '36px' }, '& .MuiAutocomplete-input': { padding: '4px !important', fontSize: '14px' } }}
                                        ListboxProps={{ style: { maxHeight: 250, fontSize: '14px' } }}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params}
                                                error={submitAttempted && !state.employee && !state.selectAll}
                                                required
                                                placeholder="Search employee by name"
                                                className={submitAttempted && !state.employee && !state.selectAll ? '[&_fieldset]:!border-red-500' : ''}
                                                InputProps={{ 
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position="start" sx={{ ml: 1 }}>
                                                                <User size={16} className={submitAttempted && !state.employee && !state.selectAll ? "text-red-500" : "text-slate-400"}/>
                                                            </InputAdornment>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    )
                                                }} 
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-1 border-t border-slate-100 pt-4 mt-6">
                            <h3 className="text-blue-600 font-semibold text-sm mb-3">2. Allow Update</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Current Profile Status</label>
                                    <TextField size="small" value={state.selectAll ? '' : (employees?.find(el => el.EMP_CODE === state.employee)?.ALLOW_EDIT === 'Yes' ? 'Allowed' : employees?.find(el => el.EMP_CODE === state.employee)?.ALLOW_EDIT === 'No' ? 'Restricted' : '')} disabled className="w-full bg-slate-50" InputProps={{ startAdornment: <InputAdornment position="start"><UserCog size={16} className="text-slate-400"/></InputAdornment> }} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Update Status <span className="text-red-500">*</span></label>
                                    <div className="inline-flex rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm  max-w-sm">
                                        {[
                                            { value: "Yes", label: "Allow", color: "emerald" },
                                            { value: "No", label: "Restrict", color: "red" },
                                        ].map((item) => {
                                            const isDisabled = !state.selectAll && state.employee && employees?.find(el => el.EMP_CODE === state.employee)?.ALLOW_EDIT === item.value;
                                            return (
                                            <label
                                                key={item.value}
                                                className={`cursor-pointer px-6 py-2 text-sm font-medium transition-all flex-1 text-center ${
                                                    isDisabled ? 'opacity-50 cursor-not-allowed bg-slate-50' : ''
                                                } ${
                                                    state.allowUpdate === item.value
                                                        ? item.color === "emerald"
                                                            ? "bg-emerald-50 text-emerald-700"
                                                            : "bg-red-50 text-red-600"
                                                        : "text-slate-500 hover:bg-slate-50"
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    hidden
                                                    disabled={isDisabled}
                                                    name="allowUpdate"
                                                    value={item.value}
                                                    checked={state.allowUpdate === item.value}
                                                    onChange={handleChange}
                                                />
                                                {item.label}
                                            </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}
            {currentView === 'add_remove_admin' && (
                <AddRemoveAdmin setCurrentView={setCurrentView} />
            )}

            {currentView === 'change_reporting_manager' && (
                <ChangeReportingManager setCurrentView={setCurrentView} />
            )}

            {currentView === 'reset_password' && (
                <ResetPasswordForm setCurrentView={setCurrentView} />
            )}
            
            {currentView === 'reset_2fa' && (
                <Reset2FAForm setCurrentView={setCurrentView} />
            )}
            {currentView === 'update_user_profile' && (
                <div className=''>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex-1 flex flex-col pt-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4 border-b border-slate-100 pb-3 shrink-0">
                        <div className="flex items-center gap-3">
                            <button type="button" onClick={handleBackToMain} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors -ml-2">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                            </button>
                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                                <UserPen size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-slate-800">Update Employee Profile</h2>
                                <p className="text-xs text-slate-500 mt-0.5">Edit employee details</p>
                            </div>
                        </div>
                    </div>
                    <UpdateUser />
                </div>
                </div>
            )}
        </div>
        </div>
            )}
        </div>
        </LetterLoadingContext.Provider>
    )
}

export default EmployeeTabSA
